import { ref } from "vue"
import { useFirestore } from "vuefire"
import { collection, doc, runTransaction, serverTimestamp, Timestamp } from "firebase/firestore"
import {
  type CreateCustomerFeedback,
  CreateCustomerFeedbackSchema,
  type CustomerFeedbackSnapshot,
} from "@wowpress/types"
import { useLogEvent } from "@/composables/useAnalytics"

// useFeedback 컴포저블 함수
export function useFeedback() {
  const db = useFirestore()
  const loading = ref(false) // 피드백 저장 로딩 상태
  const error = ref<Error | null>(null) // 피드백 저장 에러 상태

  /**
   * 고객 피드백을 저장하고 관련 티켓 문서를 업데이트합니다.
   * @param feedbackData - 저장할 피드백 데이터 (CustomerFeedback 인터페이스 준수)
   * @returns 성공 시 true, 실패 시 false
   */
  const saveFeedback = async (feedbackData: CreateCustomerFeedback): Promise<boolean> => {
    // Firestore 인스턴스 또는 필수 데이터 확인
    if (!db) {
      error.value = new Error("Firestore 인스턴스가 없습니다.")
      console.error("[useFeedback] Firestore 인스턴스 없음")
      return false
    }
    try {
      // 피드백 데이터 검증
      CreateCustomerFeedbackSchema.parse(feedbackData)
    } catch (e) {
      error.value = new Error("피드백 데이터 검증 실패: " + (e as Error).message)
      console.error("[useFeedback] 피드백 데이터 검증 실패:", e)
      return false
    }

    loading.value = true // 로딩 시작
    error.value = null // 이전 에러 초기화

    try {
      const feedbacksColRef = collection(db, "customerFeedbacks")
      const ticketDocRef = doc(db, "tickets", feedbackData.ticketId)

      console.log("[useFeedback] Saving feedback for ticket:", feedbackData.ticketId)

      // 트랜잭션을 사용하여 두 업데이트를 원자적으로 처리
      await runTransaction(db, async (transaction) => {
        // 1. 새로운 피드백 문서 참조 생성 (ID 자동 생성)
        const newFeedbackRef = doc(feedbacksColRef)

        // 2. customerFeedbacks 컬렉션에 저장할 데이터 준비 (createdAt 추가)
        const feedbackToSave = {
          ...feedbackData,
          createdAt: serverTimestamp() as Timestamp, // 서버 타임스탬프 사용
        }

        // 3. 피드백 문서 생성
        transaction.set(newFeedbackRef, feedbackToSave)

        // 4. 티켓 문서 업데이트 (customerFeedbackId 와 updatedAt 추가)
        transaction.update(ticketDocRef, {
          customerFeedbackId: newFeedbackRef.id, // 생성된 피드백 문서 ID 저장
          updatedAt: serverTimestamp() as Timestamp, // 티켓 업데이트 시간 갱신
          feedback: {
            choiceRating: feedbackData.choiceRating,
            directRating: feedbackData.directRating,
            improvementText: feedbackData.improvementText,
            userId: feedbackData.userId,
            createdAt: serverTimestamp(),
          } as CustomerFeedbackSnapshot,
        })
      })

      useLogEvent("feedback", {
        choiceRating: feedbackData.choiceRating,
        directRating: feedbackData.directRating,
        improvementText: feedbackData.improvementText,
        userId: feedbackData.userId,
      })

      console.log("[useFeedback] Feedback saved successfully, ticket updated.")
      return true // 성공 반환
    } catch (e: any) {
      console.error("[useFeedback] Error saving feedback:", e)
      error.value = e // 에러 상태 업데이트
      return false // 실패 반환
    } finally {
      loading.value = false // 로딩 종료
    }
  }

  // 컴포저블 반환 값
  return {
    loading, // 외부에서 로딩 상태 확인 가능
    error, // 외부에서 에러 상태 확인 가능
    saveFeedback, // 피드백 저장 함수
  }
}
